import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { CheckIcon, ArrowDownIcon, XCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { ImportLineItem, LineClassification } from '../types';
import {
  getAvailableLots,
  getAvailableEnsembles,
  getAvailableSousEnsembles,
  getLotCategoryLabel,
} from '../data/classifyingData';
import SearchableDropdown from './SearchableDropdown';

interface ClassifyingBatchBarProps {
  selectedIds: Set<string>;
  lines: ImportLineItem[];
  onBatchClassify: (ids: string[], classification: Partial<LineClassification>) => void;
  onClearClassification: (ids: string[]) => void;
  onClickLine: (id: string | null) => void;
}

const ClassifyingBatchBar: React.FC<ClassifyingBatchBarProps> = ({
  selectedIds,
  lines,
  onBatchClassify,
  onClearClassification,
  onClickLine,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [batchLot, setBatchLot] = useState<string | null>(null);
  const [batchEnsemble, setBatchEnsemble] = useState<string | null>(null);
  const [batchSousEnsemble, setBatchSousEnsemble] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const selectedCount = selectedIds.size;

  // Reset feedback when selection changes
  useEffect(() => setApplied(false), [selectedIds]);

  // Close panel on click outside
  useEffect(() => {
    if (!expanded) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [expanded]);

  const lots = useMemo(() => getAvailableLots(), []);
  const ensembles = useMemo(() => getAvailableEnsembles(batchLot), [batchLot]);
  const sousEnsembles = useMemo(() => getAvailableSousEnsembles(batchEnsemble), [batchEnsemble]);

  const handleApply = useCallback(() => {
    const ids = Array.from(selectedIds);
    const partial: Partial<LineClassification> = {};
    if (batchLot !== null) partial.lotId = batchLot;
    if (batchEnsemble !== null) partial.ensembleId = batchEnsemble;
    if (batchSousEnsemble !== null) partial.sousEnsembleId = batchSousEnsemble;
    if (Object.keys(partial).length > 0) {
      onBatchClassify(ids, partial);
      setBatchLot(null);
      setBatchEnsemble(null);
      setBatchSousEnsemble(null);
      setApplied(true);
      setExpanded(false);
    }
  }, [selectedIds, batchLot, batchEnsemble, batchSousEnsemble, onBatchClassify]);

  const handleLotChange = useCallback((lotId: string | null) => {
    setBatchLot(lotId);
    setBatchEnsemble(null);
    setBatchSousEnsemble(null);
  }, []);

  const handleEnsembleChange = useCallback((ensembleId: string | null) => {
    setBatchEnsemble(ensembleId);
    setBatchSousEnsemble(null);
  }, []);

  // "Next unclassified" logic
  const nextUnclassified = useMemo(() => {
    return lines.find(
      (l) => !l.isTitle && (!l.classification || l.classification.status === 'unclassified')
    );
  }, [lines]);

  const handleNextUnclassified = useCallback(() => {
    if (nextUnclassified) onClickLine(nextUnclassified.id);
  }, [nextUnclassified, onClickLine]);

  const handleClearSelected = useCallback(() => {
    onClearClassification(Array.from(selectedIds));
  }, [selectedIds, onClearClassification]);

  return (
    <div className="flex items-center gap-1.5" ref={panelRef}>
      {/* Next unclassified */}
      <button
        onClick={handleNextUnclassified}
        disabled={!nextUnclassified}
        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 hover:text-violet-700 hover:bg-violet-50 disabled:text-gray-300 disabled:hover:bg-transparent rounded-md transition-colors"
      >
        <ArrowDownIcon className="w-3 h-3" />
        Non classée
      </button>

      {/* Batch classify (dropdown trigger) */}
      {selectedCount > 0 && (
        <div className="relative">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-md transition-colors"
          >
            Classer ({selectedCount})
            <ChevronDownIcon className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>

          {/* Absolute dropdown panel */}
          {expanded && (
            <div className="absolute top-full left-0 mt-1 z-40 w-[480px] bg-white border border-gray-200 rounded-lg shadow-xl p-3">
              <div className="flex items-end gap-2">
                <div className="flex-1 min-w-0">
                  <label className="text-[10px] text-gray-500 mb-0.5 block">Lot</label>
                  <SearchableDropdown
                    options={lots}
                    value={batchLot}
                    onChange={handleLotChange}
                    getLabel={(l) => l.nom}
                    getId={(l) => l.id}
                    getGroup={(l) => getLotCategoryLabel(l.id)}
                    placeholder="Lot…"
                    size="sm"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <label className="text-[10px] text-gray-500 mb-0.5 block">Ensemble</label>
                  <SearchableDropdown
                    options={ensembles}
                    value={batchEnsemble}
                    onChange={handleEnsembleChange}
                    getLabel={(e) => e.nom}
                    getId={(e) => e.id}
                    placeholder="Ensemble…"
                    disabled={!batchLot}
                    disabledHint="Sélectionnez un lot d'abord"
                    size="sm"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <label className="text-[10px] text-gray-500 mb-0.5 block">Sous-ens.</label>
                  <SearchableDropdown
                    options={sousEnsembles}
                    value={batchSousEnsemble}
                    onChange={setBatchSousEnsemble}
                    getLabel={(se) => se.nom}
                    getId={(se) => se.id}
                    placeholder="Sous-ens…"
                    disabled={!batchEnsemble}
                    disabledHint="Sélectionnez un ensemble d'abord"
                    size="sm"
                  />
                </div>
                <button
                  onClick={handleApply}
                  disabled={!batchLot && !batchEnsemble && !batchSousEnsemble}
                  className="px-3 py-1 text-xs font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded transition-colors whitespace-nowrap"
                >
                  Appliquer
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Clear classification */}
      {selectedCount > 0 && (
        <button
          onClick={handleClearSelected}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
        >
          <XCircleIcon className="w-3 h-3" />
          Déclasser
        </button>
      )}

      {/* Applied feedback */}
      {applied && (
        <span className="flex items-center gap-1 text-xs text-emerald-600">
          <CheckIcon className="w-3.5 h-3.5" />
        </span>
      )}
    </div>
  );
};

export default ClassifyingBatchBar;
